<template>
	<div
		class="header_section_header sticky-header"
		:class="compact ? 'is-compact' : 'is-wrap'">
		<div class="header_left">
			<div class="header_title">{{ title }}</div>
			<div class="header_description mb-4" v-if="description">
				{{ description }}
			</div>

			<!-- Sub-Navigation Filters: Mensual / Trimestral / YTD / Anual.
				Cuando el tab habilita range, el AnualCalendar vive inline a la
				derecha del grupo — vive independiente de los períodos (no
				participa del `is-active`), pero comparte fila para que el
				usuario lo lea como parte del cluster de "controles del período
				analizado". -->
			<div class="period_filters_row">
				<div class="sub_nav_filters">
					<button
						v-for="period in filterOptions"
						:key="period.id"
						class="filter_btn"
						:class="{ 'is-active': filterPeriod === period.id }"
						@click="$emit('update-filter-period', period.id)">
						{{ period.label }}
					</button>
				</div>
				<AnualCalendar v-if="calendarMode === 'range' && ['month', 'quarter'].includes(filterPeriod)"
					class="range_inline_calendar"
					mode="range"
					:range-value="dateRange"
					range-trigger-label="Rango"
					:disable-newer-dates="true"
					:disabled-from="getNow"
					:min-date="dataFirstMonth"
					:align-right="false"
					@range-change="(range) => $emit('update-date-range', range)" />
				<!-- YTD: las columnas son años recortados a un mes de corte común.
					El selector combinado (modo "ytd") elige en un solo dropdown el
					span de AÑOS (paso 1) y luego el MES de corte (paso 2), aplicado
					por igual a cada año. Emite `ytd-change` { from, to, endMonth };
					lo desdoblamos en update-year-range (el span) + update-end-month
					(año `to` + mes de corte). Default = el rango de Anual + el mes
					de `endMonth`. -->
				<AnualCalendar v-else-if="calendarMode === 'range' && filterPeriod === 'ytd'"
					class="range_inline_calendar"
					mode="ytd"
					range-trigger-label="YTD"
					:range-value="yearRange"
					:value="endMonth"
					:disable-newer-dates="true"
					:disabled-from="getNow"
					:min-date="dataFirstMonth"
					:align-right="false"
					@ytd-change="onYtdChange" />
				<!-- Anual: las columnas son años calendario. En vez del default
					fijo (últimos N años) el usuario elige un span de años con la
					misma mecánica de rango — grilla de años en lugar de meses,
					mismo chrome del trigger ("Años  2021 – 2025"). -->
				<AnualCalendar v-else-if="calendarMode === 'range' && filterPeriod === 'year'"
					class="range_inline_calendar"
					mode="range"
					granularity="year"
					range-trigger-label="Años"
					:range-value="yearRange"
					:disable-newer-dates="true"
					:disabled-from="getNow"
					:min-date="dataFirstMonth"
					:align-right="false"
					@range-change="(range) => $emit('update-year-range', range)" />
			</div>
		</div>

		<!-- Slot opcional para sub-filters extra (Cuentas/Clientes en Ventas,
			MultiSelectDropdown de contrapartes en Gastos/Costos/Activos/NoOper).
			El padre define markup y eventos — TabHeader no asume estructura. -->
		<div v-if="$slots['center-sub-nav']" class="center_sub_nav">
			<slot name="center-sub-nav" />
		</div>

		<div class="right_actions is-flex">
			<!-- Single-mode: el calendar vive en right_actions (todos los tabs
				menos Ventas). Range-mode: ya se renderizó inline al lado de
				los chips de período arriba; este slot queda sin calendar. -->
			<div v-if="calendarMode !== 'range'"
				class="calendar_picker is-flex is-align-items-center mr-3">
				<AnualCalendar
					class="calendar"
					:value="endMonth"
					@date-change="(date) => $emit('update-end-month', date)"
					:disable-newer-dates="true"
					:disabled-from="getNow"
					:min-date="dataFirstMonth"
					format-label-date="MMMM YYYY"
					:align-right="true" />
			</div>
			<!-- Exportar: encola el libro completo (EERR, Balance y Flujo de caja
				× mensual/trimestral/anual) al mes de corte y lo descarga cuando
				está listo. El estado vive en reportsFilters: el polling sigue
				aunque el usuario cambie de tab. -->
			<div v-if="isExporting" class="export_progress" role="status" aria-live="polite">
				<span class="export_progress_text">
					Generando el archivo… {{ exportPercent }} %
				</span>
				<span v-if="exportSheet" class="export_progress_sheet">{{ exportSheet }}</span>
				<span class="export_progress_bar">
					<span class="export_progress_fill" :style="{ width: exportPercent + '%' }" />
				</span>
			</div>
			<button v-else class="theme_btn_white is-flex is-align-items-center export_btn"
				type="button"
				title="Descargar el reporte en Excel"
				@click="exportReport">
				<DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
				Exportar
			</button>
		</div>

		<!-- Barra inferior del header (parte del bloque sticky): título de la
			tabla + filtro. Al vivir dentro del header sticky queda siempre
			visible al hacer scroll. Opt-in: sólo se renderiza si el tab pasa
			`table-title` o el slot de acciones (Ventas/Costos/Gastos). -->
		<div v-if="tableTitle || $slots['table-bar-actions']" class="header_table_bar">
			<!-- `table-title-collapsible`: el título ES el toggle de los gráficos
				(chevron a la derecha), igual que antes pero ahora en el header
				sticky. El cuerpo de los gráficos vive abajo en el tab y se controla
				con `tableExpanded`. Sin la prop, el título es texto plano. -->
			<button v-if="tableTitleCollapsible"
				type="button"
				class="table_bar_toggle"
				:aria-expanded="String(tableExpanded)"
				@click="$emit('toggle-table')">
				<span class="table_bar_title">{{ tableTitle }}</span>
				<svg class="table_bar_chevron" :class="{ 'is-collapsed': !tableExpanded }"
					viewBox="0 0 20 20" fill="none" aria-hidden="true">
					<path d="M5 8l5 5 5-5" stroke="currentColor" stroke-width="1.5"
						stroke-linecap="round" stroke-linejoin="round" />
				</svg>
			</button>
			<span v-else class="table_bar_title">{{ tableTitle }}</span>
			<div v-if="$slots['table-bar-actions']" class="table_bar_actions">
				<slot name="table-bar-actions" />
			</div>
		</div>
	</div>
</template>

<script>
import AnualCalendar from "@/components/classification/AnualCalendar";
import DownloadFile from "@/components/svg/DownloadFile.vue";

export default {
	name: "TabHeader",
	components: { AnualCalendar, DownloadFile },
	props: {
		title: { type: String, required: true },
		description: { type: String, default: "" },
		// Título de la tabla (p.ej. "Ventas por cuenta") que se muestra en la
		// barra inferior del header sticky, junto al slot `table-bar-actions`
		// (el filtro de cuentas/items). Vacío = la barra no se renderiza.
		tableTitle: { type: String, default: "" },
		// Opt-in: el `tableTitle` funciona como toggle (chevron) que abre/cierra
		// los gráficos del tab. Emite `toggle-table`; el chevron refleja
		// `tableExpanded`.
		tableTitleCollapsible: { type: Boolean, default: false },
		tableExpanded: { type: Boolean, default: true },
		// Valor activo del set de períodos (Mensual/Trimestral/YTD/Anual).
		filterPeriod: { type: String, required: true },
		filterOptions: { type: Array, required: true },
		endMonth: { type: Date, required: true },
		// getNow es el corte "ahora" pasado al AnualCalendar — los tabs lo
		// expone como computed que devuelve `moment().toDate()`, por eso
		// llega como Date (no Function).
		getNow: { type: Date, required: true },
		// Compact = sin wrap, header en una sola línea. ResultadosTab y
		// RentabilidadTab no tienen `center_sub_nav` y caben en una fila.
		// El resto (Ventas/Gastos/Costos/Activos/NoOper) hace wrap para
		// que el sub-nav baje a una segunda fila cuando no entra horizontal.
		compact: { type: Boolean, default: false },
		// Modo del calendario del header. "single" (default) = picker de mes
		// único (todos los tabs salvo Ventas). "range" = picker de rango de
		// meses (Ventas — el usuario elige [from, to] explícito y eso
		// reemplaza el dropdown de "Último año / 24 meses / ..."). El padre
		// pasa `dateRange` cuando el modo es "range".
		calendarMode: {
			type: String,
			default: "single",
			validator: (v) => ["single", "range"].includes(v),
		},
		dateRange: { type: Object, default: null },
		// Rango de años activo para el picker de Anual (granularidad "year").
		// `{ from: Date, to: Date }`. El padre lo provee como computed (custom o
		// default derivado) cuando el modo es "range"; emite `update-year-range`.
		yearRange: { type: Object, default: null },
	},
	computed: {
		// Piso de los 4 calendarios: primer mes con datos de la empresa
		// (`reportsFilters.dataFirstMonth`, cargado por DashboardReports desde
		// el backend). Se lee directo del store — pasarlo como prop obligaría a
		// cablear los 11 tabs con el mismo valor. Acceso defensivo: TabHeader
		// es presentacional y sus specs lo montan sin store; sin módulo el piso
		// queda null (= sin piso, comportamiento histórico).
		dataFirstMonth() {
			const reportsFilters = this.$store && this.$store.state.reportsFilters;
			return reportsFilters ? reportsFilters.dataFirstMonth : null;
		},
		isExporting() {
			const reportsFilters = this.$store && this.$store.state.reportsFilters;
			return Boolean(reportsFilters && reportsFilters.exportState === "running");
		},
		exportPercent() {
			const reportsFilters = this.$store && this.$store.state.reportsFilters;
			const progress = reportsFilters && reportsFilters.exportProgress;
			return progress ? Math.round(progress.percent) : 0;
		},
		exportSheet() {
			const reportsFilters = this.$store && this.$store.state.reportsFilters;
			const progress = reportsFilters && reportsFilters.exportProgress;
			return progress ? progress.sheet : "";
		},
	},
	methods: {
		exportReport() {
			if (this.isExporting || !this.$store) return;
			this.$store.dispatch("reportsFilters/exportReport");
		},
		// El selector combinado de YTD emite un único payload con el span de
		// años + el mes de corte (endMonth = año `to` + mes). Lo desdoblamos en
		// los dos eventos que los tabs ya cablean (update-year-range alimenta
		// `customYearRange`; update-end-month, el corte).
		onYtdChange({ from, to, endMonth }) {
			this.$emit("update-year-range", { from, to });
			this.$emit("update-end-month", endMonth);
		},
	},
};
</script>

<style scoped lang="scss">
.header_section_header {
	// Ancla directo al header global (publicado en Sidebar.vue). No hay
	// barra de tabs intermedia que medir, así que no necesitamos
	// --tab-sticky-top.
	position: sticky;
	top: var(--app-chrome-top, 0px);
	z-index: 4;
	background-color: var(--report-bg);
	border-bottom: 1px solid var(--report-border);
	padding-bottom: 8px;
	// margin-bottom: 1.5rem;
}

// Barra inferior del header: flex-basis 100% la fuerza a su propia línea dentro
// del header (is-wrap), debajo del título/períodos/sub-nav. Al ser hija del
// .header_section_header sticky, queda siempre visible al hacer scroll.
.header_table_bar {
	flex: 0 0 100%;
	width: 100%;
	display: flex;
	align-items: center;
	gap: 12px;
}

.table_bar_title {
	font-size: 18px;
	font-weight: 600;
	color: var(--report-text);
}

// Título-como-toggle: chrome thin, igual que el toggle de CollapsibleSection.
// El texto se alinea con el borde izquierdo (margin negativo compensa el padding).
.table_bar_toggle {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 4px 8px;
	margin-left: -8px;
	background: transparent;
	border: none;
	border-radius: 8px;
	cursor: pointer;
	color: var(--report-text);
	transition: background-color 160ms ease;

	&:hover {
		background: var(--report-surface);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}
}

.table_bar_chevron {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	color: var(--report-text-muted-strong);
	transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}

.table_bar_chevron.is-collapsed {
	transform: rotate(-90deg);
}

.table_bar_actions {
	// Empuja el filtro al extremo derecho de la barra.
	margin-left: auto;
}

.is-compact {
	display: flex;
	flex-direction: row;
	// wrap: la header_table_bar (flex-basis 100%) necesita envolver para caer
	// a su propia fila debajo de título/Exportar — sin esto quedaba en la
	// misma línea, pegada al botón Exportar (tabs de balance por nivel).
	flex-wrap: wrap;
	row-gap: 8px;
	justify-content: space-between;
	align-items: flex-end;
}

.is-wrap {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 24px;
	align-items: flex-end;

	.header_left { display: flex; flex-direction: column; }
	.center_sub_nav {
        margin-left: auto;
        margin-right: auto;
    }
	.right_actions { margin-left: auto; }

	@media (max-width: 600px) {
		.center_sub_nav, .right_actions { margin-left: 0; }
	}
}

.header_title {
	font-size: 32px;
	font-weight: 700;
	color: var(--report-text);
	margin-bottom: 4px;
}

.header_description {
	color: var(--report-text-muted);
	font-size: 14px;
}

.period_filters_row {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.range_inline_calendar {
	// El calendar usa block layout adentro (.custom-dropdown wraps absolute).
	// Acá lo dejamos como inline-block para que el dropdown anclado se
	// posicione respecto al chip, no respecto al contenedor flex.
	display: inline-block;
}

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

// Los filtros consumen su acento propio (--report-filter-*) con fallback al
// primary/surface del tema (resuelto por elemento): Ventas/KPIs lo overridean
// con el teal vivo de marca; los demás tabs conservan su color de siempre.
.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-filter-wash, var(--report-surface));
	}

	&.is-active {
		background: var(--report-filter-accent, var(--report-primary));
		color: var(--report-bg);

		&:hover {
			background: var(--report-filter-accent-hover, var(--report-primary-hover));
		}
	}
}

.calendar_picker {
	background: var(--report-primary);
	color: var(--report-bg);
	border-radius: 8px;
	font-weight: 500;

	// !important: el label vive en el subárbol del AnualCalendar (:deep), cuyas
	// reglas propias del trigger ganarían por especificidad sin esto.
	:deep(.calendar-label) {
		color: var(--report-bg) !important;
	}
}

.theme_btn_white {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 8px 16px;
	font-weight: 500;
	color: var(--report-text-strong-2);
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	cursor: pointer;

	&:hover {
		background: var(--report-surface);
	}

	&:disabled {
		opacity: 0.6;
		cursor: progress;
	}
}

// Progreso del export: reemplaza al botón mientras la task genera el
// archivo y desaparece cuando el navegador empieza la descarga.
.export_progress {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 220px;
	padding: 6px 12px;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	background: var(--report-surface);
	font-size: 0.85rem;
	color: var(--report-text-strong-2);
}

.export_progress_sheet {
	font-size: 0.75rem;
	color: var(--report-text-muted, var(--report-text-strong-2));
}

.export_progress_bar {
	display: block;
	height: 4px;
	border-radius: 2px;
	background: var(--report-border);
	overflow: hidden;
}

.export_progress_fill {
	display: block;
	height: 100%;
	background: var(--report-primary, currentColor);
	transition: width 0.4s ease;
}
</style>
